import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@ideoz/ui';
import { Sparkles } from 'lucide-react';
import {
  DESIGN_SYSTEM_SECTIONS,
  type DesignSystemSectionId,
} from '../designSystemConstants';

export type DesignSystemSectionNavProps = {
  onSectionClick: (id: DesignSystemSectionId) => void;
  onExtractClick?: () => void;
  className?: string;
};

export function DesignSystemSectionNav({
  onSectionClick,
  onExtractClick,
  className,
}: DesignSystemSectionNavProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <nav className={cn('flex h-full flex-col', className)} aria-label={t('designSystem.onThisPage')}>
      <div className="flex-1">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          {t('designSystem.onThisPage')}
        </p>
        <ul className="space-y-0.5">
          {DESIGN_SYSTEM_SECTIONS.map(({ id, icon: Icon }) => (
            <li key={id}>
              <button
                type="button"
                onClick={() => onSectionClick(id)}
                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800/60 dark:hover:text-gray-100"
              >
                <Icon className="size-4 shrink-0 text-gray-400 opacity-70" />
                {t(`designSystem.sections.${id}`)}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 rounded-xl border border-gray-200/70 bg-gray-50/80 p-3.5 dark:border-gray-700 dark:bg-gray-800/40">
        <div className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-gray-800 dark:text-gray-100">
          <Sparkles className="size-3.5 text-primary-600" />
          {t('designSystem.fasterRoute')}
        </div>
        <p className="mb-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
          {t('designSystem.extractNavHint')}
        </p>
        <button
          type="button"
          onClick={onExtractClick}
          className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
        >
          {t('designSystem.uploadScreenshots')}
        </button>
      </div>
    </nav>
  );
}

export default DesignSystemSectionNav;
